import clsx from 'clsx';
import React from 'react';

const SettingsCardContext = React.createContext(false);

/** Whether content renders inside a SettingsCard, e.g. to draw a row rule. */
export const useIsInSettingsCard = () => React.useContext(SettingsCardContext);

interface SettingsCardProps {
  /**
   * `rows` divides SettingsRows with a rule; `fields` pads the card and spaces
   * SettingsFields, for inputs that need the card's full width.
   */
  layout?: 'rows' | 'fields';
  children: React.ReactNode;
}

/**
 * A container query on the card, rather than a viewport breakpoint, decides
 * when rows stack, so a card in a narrow panel stacks the same way it would on
 * a phone.
 */
export const SettingsCard = ({
  layout = 'rows',
  children,
}: SettingsCardProps) => (
  <div
    className={clsx(
      '@container overflow-hidden rounded-[4px] bg-white shadow-[inset_0_0_0_1px_var(--color-gray-200)]',
      // Without Tailwind's preflight, divide-solid gives every row a 3px side
      // border too (the browser's default width), so the sides are zeroed.
      layout === 'rows'
        ? 'divide-y divide-solid divide-gray-200 *:border-x-0'
        : 'flex flex-col gap-3.5 p-4',
    )}
  >
    <SettingsCardContext.Provider value>
      {children}
    </SettingsCardContext.Provider>
  </div>
);
